import {naturalSort, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import {noop} from 'jquery';
import React, {useEffect, useMemo} from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {TriangleLocation} from 'notebook/tables/errors/ErrorTriangle';
import IllegalErrorTriangle from 'notebook/tables/errors/IllegalErrorTriangle';
import {
  Cell,
  Table,
  TableColumnType,
  BaseTypedValueType,
  ColumnConstraint,
  TableDimension,
} from 'notebook/tables/TableConstants';
import {getBooleanCellValue} from 'notebook/tables/typed-values/DataTypes';
import {errorIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {SAMPLE_TYPES_URL} from 'sample-types/network/SampleTypeDAO';
import {CLINICAL_STUDIES_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {
  SampleDefinitionTableSampleTypesQuery_allSampleTypes_SampleTypeNode as SampleTypeNode,
  SampleDefinitionTableSampleTypesQuery,
  SampleDefinitionTableSampleTypesQueryVariables,
} from 'studies/typed/clinical/__generated/SampleDefinitionTableSampleTypesQuery';
import {TreatmentGroup} from 'studies/typed/clinical/TreatmentGroupTable';
import {Counter, useStudyDesignItemTableSharedProps} from 'studies/typed/utils/StudyDesignItemUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {COLORS} from 'ui-library/ColorPickerConstants';
import {SILVER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Link from 'ui-library/Link';
import {Optional} from 'util/Types';

export interface SampleDefinition {
  id?: string;
  label: string;
  visit: string;
  sampleTypeLabel: string;
  treatmentGroupLabel: string;
  allowsNewSubjects: boolean;
  validationState?: SampleDefinitionValidationState;
  isReadyForExecution: boolean;
}

/**
 * Tracks validation state for sample definitions.
 *
 * Any truthy value in this validation state is a blocking validation error for saving the study design.
 */
export interface SampleDefinitionValidationState {
  isMissingLabel: boolean;
  isLabelLengthOverLimit: boolean;
  isDuplicateLabel: boolean;
  isMissingVisit: boolean;
  isVisitLengthOverLimit: boolean;
  isMissingSampleTypeLabel: boolean;
  isSampleTypeLabelOutsideAvailableOptions: boolean;
  isMissingTreatmentGroupLabel: boolean;
  isTreatmentGroupLabelOutsideAvailableOptions: boolean;
  isDuplicateSampleMatchingCriteria: boolean;
  isMissingNewSubjectsPerTreatmentGroup: boolean;
}

export type PersistedSampleDefinition = SampleDefinition & Required<Pick<SampleDefinition, 'id'>>;

export const INACTIVE_SAMPLE_DEFINITION_TOOLTIP = 'Proceed to execution to finalize this sample definition';
export const NEW_SUBJECTS_COLUMN_INFO_ICON_TOOLTIP =
  'Identify when new subjects are expected to be enrolled.';
export const NEW_SUBJECTS_PER_TREATMENT_GROUP_ERROR_MESSAGE =
  'Each treatment group requires at least one sample definition that allows new subjects.';

interface SampleDefinitionTableProps {
  tableTitle: string;
  sampleDefinitions: Array<SampleDefinition>;
  setSampleDefinitions: (sampleDefinitions: Array<SampleDefinition>) => void;
  sampleTypes: Array<SampleTypeNode>;
  treatmentGroups: Array<TreatmentGroup>;
  locked: boolean;
  viewer: boolean;
}

/**
 * Returns all sample types (including those that are archived), with the fields needed for use in the
 * SampleDefinitionTable.
 *
 * @package exported for testing.
 */
export const SAMPLE_DEFINITION_TABLE_SAMPLE_TYPES_QUERY = gql`
  query SampleDefinitionTableSampleTypesQuery @skipAudit @engComponent(name: STUDIES) {
    allSampleTypes {
      ... on SampleTypeNode {
        id
        label
        archiveData {
          archived
        }
      }
    }
  }
`;

/**
 * Returns an ErrorTriangle indicating an illegal value error with the given error text.
 *
 * @param errorText User-facing error message
 *
 * @package exported for testing.
 */
export function makeIllegalValueErrorTriangle(errorText: string): JSX.Element {
  return (
    <IllegalErrorTriangle
      error={{type: 'Value error', message: errorText}}
      location={TriangleLocation.CELL}
    />
  );
}

export function makeAggregateIllegalErrorTriangle(errorText: string): JSX.Element {
  return (
    <IllegalErrorTriangle error={{type: 'Error', message: errorText}} location={TriangleLocation.AGGREGATE} />
  );
}

/**
 * Return a basic boolean Cell with the supplied value as its value.
 *
 * @package exported for testing.
 */
export function makeBooleanCell(value: boolean, {locked}: {locked: boolean}): Cell {
  return {
    text: '',
    backgroundColor: COLORS.WHITE,
    locked,
    align: 'left',
    typedValue: {
      type: BaseTypedValueType.BOOLEAN,
      booleanValue: value,
    },
  };
}

/**
 * Return a basic text Cell with the supplied text as text.
 *
 * @package exported for testing.
 */
export function makeTextCell(
  text: string,
  {locked}: {locked: boolean},
  maybeErrorTriangle: JSX.Element | null = null
): Cell {
  return {
    text,
    typedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: text,
    },
    locked,
    bold: false,
    align: 'left',
    backgroundColor: COLORS.WHITE,
    userAnnotationIndicator: maybeErrorTriangle,
  };
}

/**
 * Create a Counter for identifying uniqueness violations in sample definition labels.
 *
 * @returns Counter providing case-insensitive counts of the labels of the input sample definitions.
 * @package exported for testing.
 */
export function getSampleDefinitionLabelCounter(
  sampleDefinitions: Array<SampleDefinition>
): Counter<SampleDefinition> {
  return new Counter(sampleDefinitions, (d) => d.label.toLowerCase());
}

/**
 * Create a map providing number of treatment groups with allowed new subjects.
 *
 * @returns map providing number of treatment groups assigned to a sample definition with allowed new subjects.
 * @package exported for testing.
 */
export function getAllowsNewSubjectsPerTreatmentGroupMap(
  sampleDefinitions: Array<SampleDefinition>
): Map<string, number> {
  const numTreatmentGroupsWithNewSubjects = sampleDefinitions.reduce((acc, sampleDefinition) => {
    if (!acc.has(sampleDefinition.treatmentGroupLabel)) {
      acc.set(sampleDefinition.treatmentGroupLabel, 0);
    }
    if (sampleDefinition.allowsNewSubjects) {
      const count = acc.get(sampleDefinition.treatmentGroupLabel) ?? 0;
      acc.set(sampleDefinition.treatmentGroupLabel, count + 1);
    }
    return acc;
  }, new Map<string, number>());
  return numTreatmentGroupsWithNewSubjects;
}

/**
 * Create a Counter for identifying uniqueness violations in sample definition matching criteria. Each sample
 * definition must have a unique combination of visit, sample type, and treatment group (the parts used to
 * match against incoming samples).
 *
 * @returns Counter providing counts of matching criteria for the input sample definitions.
 * @package exported for testing.
 */
export function getSampleDefinitionMatchingCriteriaCounter(
  sampleDefinitions: Array<SampleDefinition>
): Counter<SampleDefinition> {
  function getMatchingCriteria(sampleDefinition: SampleDefinition): string {
    return JSON.stringify([
      sampleDefinition.visit.toLowerCase(),
      sampleDefinition.sampleTypeLabel,
      sampleDefinition.treatmentGroupLabel,
    ]);
  }
  return new Counter<SampleDefinition>(sampleDefinitions, getMatchingCriteria);
}

/**
 * Get the validation state for the given sample definition, given the counts of existing sample definition
 * labels (for uniqueness constraints), and the available sample types and treatment groups.
 *
 * @package exported for testing.
 */
export function getValidationStateForSampleDefinition(
  sampleDefinition: SampleDefinition,
  sampleDefinitionLabelCounter: Counter<SampleDefinition>,
  sampleDefinitionMatchingCriteriaCounter: Counter<SampleDefinition>,
  availableSampleTypeLabels: Array<string>,
  availableTreatmentGroupsLabels: Array<string>,
  numTreatmentGroupsWithNewSubjects: Map<string, number>
): SampleDefinitionValidationState {
  return {
    isMissingLabel: sampleDefinition.label === '',
    isLabelLengthOverLimit:
      sampleDefinition.label.length >
      CLINICAL_STUDIES_VALIDATION_CONSTANTS.SAMPLE_DEFINITION_LABEL_MAX_LENGTH,
    isDuplicateLabel: sampleDefinitionLabelCounter.get(sampleDefinition) > 1,
    isMissingVisit: sampleDefinition.visit === '',
    isVisitLengthOverLimit:
      sampleDefinition.visit.length >
      CLINICAL_STUDIES_VALIDATION_CONSTANTS.SAMPLE_DEFINITION_VISIT_MAX_LENGTH,
    isMissingSampleTypeLabel: sampleDefinition.sampleTypeLabel === '',
    isSampleTypeLabelOutsideAvailableOptions: !availableSampleTypeLabels.includes(
      sampleDefinition.sampleTypeLabel
    ),
    isMissingTreatmentGroupLabel: sampleDefinition.treatmentGroupLabel === '',
    isTreatmentGroupLabelOutsideAvailableOptions: !availableTreatmentGroupsLabels.includes(
      sampleDefinition.treatmentGroupLabel
    ),
    isDuplicateSampleMatchingCriteria: sampleDefinitionMatchingCriteriaCounter.get(sampleDefinition) > 1,
    isMissingNewSubjectsPerTreatmentGroup:
      numTreatmentGroupsWithNewSubjects.get(sampleDefinition.treatmentGroupLabel) === 0 &&
      sampleDefinition.treatmentGroupLabel !== '',
  };
}

/**
 *  Update data when any individual cell is updated.
 */
export function updateTableCells(
  table: Table,
  setSampleDefinitions: (sampleDefinitions: Array<SampleDefinition>) => void,
  sampleTypes: Array<SampleTypeNode>,
  treatmentGroups: Array<TreatmentGroup>,
  persistedSampleDefinitionsById: Record<string, SampleDefinition>
): void {
  assert(table.rowData != null, 'Table must have rowData');
  const sampleDefinitionIdsByRow = table.rowData.map((rowData) =>
    rowData.rowMetadata ? rowData.rowMetadata.rowId : undefined
  );
  const newSampleDefinitions = table.table.map((sampleDefinition, index) => {
    const id = sampleDefinitionIdsByRow[index];
    return {
      id,
      label: sampleDefinition[0].text.trimEnd(),
      visit: sampleDefinition[1].text.trimEnd(),
      sampleTypeLabel: sampleDefinition[2].text,
      treatmentGroupLabel: sampleDefinition[3].text,
      allowsNewSubjects: getBooleanCellValue(sampleDefinition[4]),
      isReadyForExecution: id ? persistedSampleDefinitionsById[id].isReadyForExecution : false,
    };
  });
  const availableSampleTypeLabels = sampleTypes.map((sampleType) => sampleType.label);
  const availableTreatmentGroupsLabels = treatmentGroups.map((treatmentGroup) => treatmentGroup.label);
  const sampleDefinitionLabelCounter = getSampleDefinitionLabelCounter(newSampleDefinitions);
  const numTreatmentGroupsWithNewSubjects = getAllowsNewSubjectsPerTreatmentGroupMap(newSampleDefinitions);
  const sampleDefinitionMatchingCriteriaCounter =
    getSampleDefinitionMatchingCriteriaCounter(newSampleDefinitions);
  setSampleDefinitions(
    newSampleDefinitions.map((sampleDefinition) => ({
      ...sampleDefinition,
      validationState: getValidationStateForSampleDefinition(
        sampleDefinition,
        sampleDefinitionLabelCounter,
        sampleDefinitionMatchingCriteriaCounter,
        availableSampleTypeLabels,
        availableTreatmentGroupsLabels,
        numTreatmentGroupsWithNewSubjects
      ),
    }))
  );
}

function makeInitialSampleDefinitionTable(
  tableTitle: string,
  sampleDefinitions: Array<SampleDefinition>,
  sampleTypeOptions: Array<string>,
  treatmentGroups: Array<TreatmentGroup>,
  locked: boolean,
  viewer: boolean
): Table {
  const columnLabels = [
    'Sample definition',
    'Visit',
    'Sample type',
    'Treatment group',
    'New subjects',
  ] as const;
  // This 'as const' on columnLabels allows type checking to guarantee that the keys in
  // columnConstraintsByLabel exactly match the column labels (ensuring these stay in sync).
  const columnConstraintsByLabel: Record<(typeof columnLabels)[number], ColumnConstraint | null> = {
    'Sample definition': {
      viewer,
      columnDefinition: {
        name: 'Sample definition',
        isRequired: true,
        type: TableColumnType.STRING,
      },
    },
    Visit: {
      viewer,
      columnDefinition: {
        name: 'Visit',
        isRequired: true,
        type: TableColumnType.STRING,
      },
    },
    'Sample type': {
      viewer,
      columnDefinition: {
        name: 'Sample type',
        isRequired: true,
        type: TableColumnType.OPTION_SEARCH_SELECTOR,
        options: sampleTypeOptions,
        emptyOptionsDisplay: (
          <div>
            Add sample types in{' '}
            <Link
              onClick={() => {
                navigateToURL(SAMPLE_TYPES_URL);
              }}
            >
              feature settings
            </Link>{' '}
            or contact your Benchling administrator for assistance.
          </div>
        ),
      },
    },
    'Treatment group': {
      viewer,
      columnDefinition: {
        name: 'Treatment group',
        isRequired: true,
        type: TableColumnType.OPTION_SEARCH_SELECTOR,
        options: treatmentGroups.map((treatmentGroup) => treatmentGroup.label),
        emptyOptionsDisplay: <div>No treatment groups have been defined</div>,
      },
    },
    'New subjects': {
      viewer,
      columnDefinition: {
        name: 'New subjects',
        isRequired: true,
        type: TableColumnType.BOOLEAN,
      },
      renderedLabel: (
        <div>
          New subjects{' '}
          <Tooltip text={NEW_SUBJECTS_COLUMN_INFO_ICON_TOOLTIP}>
            <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
          </Tooltip>
        </div>
      ),
    },
  };

  const getLabelErrorTriangle = (
    validationState: SampleDefinitionValidationState | null
  ): Optional<JSX.Element> => {
    if (validationState?.isLabelLengthOverLimit) {
      const labelLengthError = `Sample definition label may not exceed ${
        CLINICAL_STUDIES_VALIDATION_CONSTANTS.SAMPLE_DEFINITION_LABEL_MAX_LENGTH
      } characters`;
      return makeIllegalValueErrorTriangle(labelLengthError);
    } else if (validationState?.isDuplicateLabel) {
      const duplicateLabelError = 'Duplicate sample definition';
      return makeIllegalValueErrorTriangle(duplicateLabelError);
    } else {
      return null;
    }
  };

  const getVisitErrorTriangle = (
    validationState: SampleDefinitionValidationState | null
  ): Optional<JSX.Element> => {
    if (validationState?.isVisitLengthOverLimit) {
      const visitLengthError = `Visit may not exceed ${
        CLINICAL_STUDIES_VALIDATION_CONSTANTS.SAMPLE_DEFINITION_VISIT_MAX_LENGTH
      } characters`;
      return makeIllegalValueErrorTriangle(visitLengthError);
    } else {
      return null;
    }
  };

  const getRowErrorTriangle = (
    validationState: SampleDefinitionValidationState | undefined
  ): Optional<JSX.Element> => {
    if (validationState?.isDuplicateSampleMatchingCriteria) {
      return makeAggregateIllegalErrorTriangle(
        'Duplicate combination of visit, sample type, and treatment group'
      );
    } else {
      return null;
    }
  };

  return {
    name: tableTitle,
    widths: columnLabels.map(() => 200),
    columnLabels: columnLabels.map((label) => label),
    columnConstraints: columnLabels.map((label) => columnConstraintsByLabel[label]),
    headerCol: true,
    headerRow: false,
    table: sampleDefinitions.map((sampleDefinition) => [
      makeTextCell(
        sampleDefinition.label,
        {locked: sampleDefinition.isReadyForExecution},
        getLabelErrorTriangle(sampleDefinition.validationState ? sampleDefinition.validationState : null)
      ),
      makeTextCell(
        sampleDefinition.visit,
        {locked: sampleDefinition.isReadyForExecution},
        getVisitErrorTriangle(sampleDefinition.validationState ? sampleDefinition.validationState : null)
      ),
      makeTextCell(
        sampleDefinition.sampleTypeLabel,
        {locked: sampleDefinition.isReadyForExecution},
        null // Sample type label error triangles are currently all handled by the column constraint
      ),
      makeTextCell(
        sampleDefinition.treatmentGroupLabel,
        {locked: sampleDefinition.isReadyForExecution},
        null // Treatment group label error triangles are currently all handled by the column constraint
      ),
      makeBooleanCell(sampleDefinition.allowsNewSubjects, {locked: sampleDefinition.isReadyForExecution}),
    ]),
    rowData: sampleDefinitions.map((sampleDefinition) => {
      const userAnnotationIndicator = getRowErrorTriangle(sampleDefinition.validationState);
      return {
        rowMetadata: {rowId: sampleDefinition.id},
        uiData: {
          userAnnotationIndicator,
          // If there is a row level error and the sample definition is not ready for execution,
          // we want to avoid showing two tooltips that conflict with each other. In this case,
          // the error tooltip takes precedence, and we don't show the unsubmitted changes tooltip.
          hasUnsubmittedChanges: userAnnotationIndicator == null && !sampleDefinition.isReadyForExecution,
          unsubmittedChangesTooltip: INACTIVE_SAMPLE_DEFINITION_TOOLTIP,
        },
        isFinalized: sampleDefinition.isReadyForExecution,
      };
    }),
    lockedDims: {allowAddRows: !locked, allowRemoveRows: !locked, allowEditCols: false},
    _hideRename: true,
    allowAddRows: !locked,
    allowRowRemoval: !locked,
    allowColumnEdits: false,
    allowNoRows: false,
    maxRows: CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_SAMPLE_DEFINITIONS_PER_DESIGN,
  };
}

export function SampleDefinitionTable(props: SampleDefinitionTableProps): JSX.Element {
  const sharedProps = useStudyDesignItemTableSharedProps();
  const sampleTypeOptions = useMemo(
    () =>
      naturalSort(
        props.sampleTypes
          .filter((sampleType) => !sampleType.archiveData.archived)
          .map((sampleType) => sampleType.label)
      ),
    [props.sampleTypes]
  );
  const persistedSampleDefinitionsById = useMemo(
    () =>
      objectFromEntries(
        props.sampleDefinitions
          .filter(
            (sampleDefinition): sampleDefinition is PersistedSampleDefinition => sampleDefinition.id != null
          )
          .map((sd) => [sd.id, sd])
      ),
    [props.sampleDefinitions]
  );

  const treatmentGroupOptions = useMemo(
    () => props.treatmentGroups.filter((treatmentGroup) => treatmentGroup.label !== ''),
    [props.treatmentGroups]
  );

  // Set to true if any sample definition has a treatment group set that does not allow new subjects across
  // all sample definitions with the treatment group.
  const isMissingNewSubjectsPerTreatmentGroup = useMemo(
    () =>
      props.sampleDefinitions.some(
        (sampleDefinition) => sampleDefinition.validationState?.isMissingNewSubjectsPerTreatmentGroup
      ),
    [props.sampleDefinitions]
  );

  // When we only have one treatment group, we want to pre-fill the treatment group label column with it
  useEffect(() => {
    if (treatmentGroupOptions.length !== 1) {
      return;
    }
    const preFilledSampleDefintionsWithTreatmentGroup = props.sampleDefinitions.map((sampleDefinition) => ({
      ...sampleDefinition,
      treatmentGroupLabel: treatmentGroupOptions[0].label,
    }));
    props.setSampleDefinitions(preFilledSampleDefintionsWithTreatmentGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentGroupOptions, props.sampleDefinitions.length]);
  const table = useMemo(
    () =>
      makeInitialSampleDefinitionTable(
        props.tableTitle,
        props.sampleDefinitions,
        sampleTypeOptions,
        treatmentGroupOptions,
        props.locked,
        props.viewer
      ),
    [
      sampleTypeOptions,
      props.tableTitle,
      props.sampleDefinitions,
      props.locked,
      treatmentGroupOptions,
      props.viewer,
    ]
  );
  return (
    <>
      <div className="u-flex-display mediocre">
        <div className={css(styles.maxWidth)}>
          <SimpleTableEditable
            {...standaloneProps}
            {...sharedProps}
            hideFormulaBox={true}
            isCompactBottomSpacing={true}
            isLeftAlignRowHeader={false}
            locked={props.locked}
            viewer={props.viewer}
            table={table}
            cid="sample-definition-table"
            updateHandler={(_, {table}: {table: Table}) => {
              updateTableCells(
                table,
                props.setSampleDefinitions,
                props.sampleTypes,
                props.treatmentGroups,
                persistedSampleDefinitionsById
              );
            }}
            storeState={noop}
            restoreState={noop}
            isCollapsible={true}
            hideRowCount={true}
            allowReorderDimensions={{
              [TableDimension.ROW]: true,
              [TableDimension.COLUMN]: false,
            }}
            dataTestAttrs={{component: 'SampleDefinitionTable', element: 'simpleTableEditable'}}
          />
        </div>
      </div>
      {isMissingNewSubjectsPerTreatmentGroup && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={NEW_SUBJECTS_PER_TREATMENT_GROUP_ERROR_MESSAGE}
        />
      )}
    </>
  );
}

export default function QueryingSampleDefinitionTable(
  props: Omit<SampleDefinitionTableProps, 'sampleTypes'>
): JSX.Element {
  const sampleTypesResult = useBenchlingQuery<
    SampleDefinitionTableSampleTypesQuery,
    SampleDefinitionTableSampleTypesQueryVariables
  >(SAMPLE_DEFINITION_TABLE_SAMPLE_TYPES_QUERY);
  // Loading state must be something that uses loadingIndicatorAttrs() for compatibility with export.
  // HorizontalThrobber (within CenteredHorizontalThrobber) does so here.
  if (sampleTypesResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  // Error state must include errorIndicatorAttrs() for compatibility with export. For this we add
  // errorIndicatorAttrs to a div; the user is notified of an error via a toast.
  else if (sampleTypesResult.type === QueryResultType.ERROR) {
    return (
      <div {...errorIndicatorAttrs()}>
        <CenteredHorizontalThrobber />
      </div>
    );
  } else {
    return <SampleDefinitionTable {...props} sampleTypes={sampleTypesResult.data.allSampleTypes} />;
  }
}

const styles = StyleSheet.create({
  maxWidth: {
    width: '100%',
  },
  infoIcon: {
    color: SILVER,
    paddingLeft: 4,
  },
});
